/**
 * soundscape.js — the whole sound of the aeroplane, synthesised.
 *
 * There are no asset files anywhere in this project and there will not be any.
 * Everything below is generated by the WebAudio graph at runtime: the engine is
 * an additive stack of oscillators locked to the crankshaft, the slipstream is
 * filtered noise, the tyres are filtered noise with a different filter, and the
 * stall warner is a square wave.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS BUILT THIS WAY
 * ---------------------------------------------------------------------------
 * 1. **Nothing is created per frame.** The graph is built once at resume() and
 *    every per-frame call is a `setTargetAtTime` on an AudioParam. Allocating
 *    nodes in the render loop is what makes browser audio crackle.
 *
 * 2. **Every parameter is ramped, never assigned.** Writing `gain.value` from
 *    a 60 Hz loop steps the signal 60 times a second and you hear each step as
 *    a click. `setTargetAtTime(target, now, tau)` hands the change to the audio
 *    thread, which interpolates it at 48 kHz.
 *
 * 3. **The engine follows RPM, not throttle.** `state.rpm` is the flight
 *    model's own spooled value, so the note lags the lever exactly as much as
 *    the propeller does. Pitch and timbre both move: opening the throttle
 *    raises the fundamental AND opens the low-pass, because a loaded engine is
 *    brighter as well as faster.
 *
 * 4. **A four-cylinder four-stroke fires twice per revolution**, and a
 *    two-blade propeller passes twice per revolution, so the dominant tone is
 *    `rpm / 60 * 2` Hz — 90 Hz at a 2,700 rpm redline, 23 Hz at 700 rpm idle.
 *    The sub-octave at `rpm / 120` is the crankshaft itself and is what makes
 *    it sound like an engine rather than a wasp.
 *
 * 5. **Browsers will not start an AudioContext without a gesture.** Construct
 *    the object whenever you like; nothing exists until `resume()` is called
 *    from inside a real click or keydown. Until then every method is a no-op
 *    and nothing throws. A machine with no WebAudio at all runs silently.
 *
 * All of it is behind try/catch: sound is never allowed to take the sim down.
 */

const ENGINE_TAU = 0.06; // s, engine parameter smoothing
const WIND_TAU = 0.12; // s, slipstream smoothing — wind is a slower thing
const MASTER_TAU = 0.05;

/** Idle and redline, matched to flightModel's defaults. */
const IDLE_RPM = 700;
const MAX_RPM = 2700;

/** Airspeed, m/s, at which the slipstream reaches full level. */
const WIND_REF_MS = 75;

const DEFAULTS = {
  masterGain: 0.5,
  muted: false,
};

/**
 * Build the soundscape. Silent until `resume()` is called from a user gesture.
 *
 * @param {Object} [opts]
 * @returns {{update: Function, resume: Function, setMuted: Function,
 *            toggleMute: Function, isMuted: () => boolean,
 *            isRunning: () => boolean, dispose: Function}}
 */
export function createSoundscape(opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };

  let ctx = null;
  let nodes = null;
  let muted = !!cfg.muted;
  let failed = false;

  /** Previous frame's onGround, so we can hear the touchdown. */
  let wasOnGround = true;
  /** Previous frame's vertical speed, m/s, for the touchdown thump's energy. */
  let lastVsMs = 0;
  let stallOn = false;

  // -------------------------------------------------------------------------
  // Graph construction
  // -------------------------------------------------------------------------

  /**
   * One second of white noise, looped. Generated rather than loaded, and
   * generated ONCE — a fresh buffer per shot would allocate 48 kB a frame.
   */
  function makeNoiseBuffer(audio) {
    const len = Math.floor(audio.sampleRate * 1.0);
    const buf = audio.createBuffer(1, len, audio.sampleRate);
    const d = buf.getChannelData(0);
    // A hand-rolled LCG, not Math.random(), so the loop point is reproducible
    // and the noise floor is identical every run.
    let s = 0x2f6e2b1;
    for (let i = 0; i < len; i += 1) {
      s = (s * 1664525 + 1013904223) >>> 0;
      d[i] = (s / 0x7fffffff - 1) * 0.5;
    }
    return buf;
  }

  function build(audio) {
    const master = audio.createGain();
    master.gain.value = 0;
    master.connect(audio.destination);

    // --- engine -----------------------------------------------------------
    // Three partials into a shared low-pass. The saw carries the harmonics
    // that read as "combustion"; the square an octave up is the exhaust bark;
    // the sine an octave down is the crank.
    const engineBus = audio.createGain();
    engineBus.gain.value = 0;

    const engineLp = audio.createBiquadFilter();
    engineLp.type = 'lowpass';
    engineLp.frequency.value = 400;
    engineLp.Q.value = 0.9;
    engineLp.connect(engineBus);

    const oscs = [];
    const oscGains = [];
    const SPEC = [
      { type: 'sine', mul: 0.5, gain: 0.55 }, // crankshaft
      { type: 'sawtooth', mul: 1.0, gain: 1.0 }, // firing order
      { type: 'square', mul: 2.0, gain: 0.22 }, // exhaust bark
      { type: 'sawtooth', mul: 3.0, gain: 0.1 },
    ];
    for (const s of SPEC) {
      const o = audio.createOscillator();
      o.type = s.type;
      o.frequency.value = 40 * s.mul;
      // A couple of cents of detune per partial stops the stack phase-locking
      // into a buzzer; a real engine is never perfectly harmonic.
      o.detune.value = (Math.random() - 0.5) * 14;
      const g = audio.createGain();
      g.gain.value = s.gain;
      o.connect(g);
      g.connect(engineLp);
      o.start();
      oscs.push(o);
      oscGains.push(g);
    }

    const noiseBuf = makeNoiseBuffer(audio);

    /** Combustion roughness — noise, band-passed at the firing frequency. */
    const engineNoise = audio.createBufferSource();
    engineNoise.buffer = noiseBuf;
    engineNoise.loop = true;
    const engineNoiseBp = audio.createBiquadFilter();
    engineNoiseBp.type = 'bandpass';
    engineNoiseBp.frequency.value = 300;
    engineNoiseBp.Q.value = 0.7;
    const engineNoiseGain = audio.createGain();
    engineNoiseGain.gain.value = 0;
    engineNoise.connect(engineNoiseBp);
    engineNoiseBp.connect(engineNoiseGain);
    engineNoiseGain.connect(engineBus);
    engineNoise.start();

    engineBus.connect(master);

    // --- slipstream -------------------------------------------------------
    // Two noise paths: a broad low rush that carries the weight, and a thin
    // high hiss that only appears at speed. Splitting them is what makes the
    // wind change CHARACTER with airspeed instead of just getting louder.
    const windSrc = audio.createBufferSource();
    windSrc.buffer = noiseBuf;
    windSrc.loop = true;

    const windLp = audio.createBiquadFilter();
    windLp.type = 'lowpass';
    windLp.frequency.value = 500;
    windLp.Q.value = 0.4;
    const windLpGain = audio.createGain();
    windLpGain.gain.value = 0;

    const windHp = audio.createBiquadFilter();
    windHp.type = 'highpass';
    windHp.frequency.value = 900;
    const windHpGain = audio.createGain();
    windHpGain.gain.value = 0;

    windSrc.connect(windLp);
    windLp.connect(windLpGain);
    windLpGain.connect(master);
    windSrc.connect(windHp);
    windHp.connect(windHpGain);
    windHpGain.connect(master);
    windSrc.start();

    // --- tyres / ground roll ---------------------------------------------
    const rollSrc = audio.createBufferSource();
    rollSrc.buffer = noiseBuf;
    rollSrc.loop = true;
    const rollLp = audio.createBiquadFilter();
    rollLp.type = 'lowpass';
    rollLp.frequency.value = 220;
    rollLp.Q.value = 1.4;
    const rollGain = audio.createGain();
    rollGain.gain.value = 0;
    rollSrc.connect(rollLp);
    rollLp.connect(rollGain);
    rollGain.connect(master);
    rollSrc.start();

    // --- touchdown thump --------------------------------------------------
    // Same looping noise buffer, its own envelope. One node, triggered by
    // ramping a gain — no per-event allocation.
    const thumpSrc = audio.createBufferSource();
    thumpSrc.buffer = noiseBuf;
    thumpSrc.loop = true;
    const thumpLp = audio.createBiquadFilter();
    thumpLp.type = 'lowpass';
    thumpLp.frequency.value = 130;
    thumpLp.Q.value = 2.2;
    const thumpGain = audio.createGain();
    thumpGain.gain.value = 0;
    thumpSrc.connect(thumpLp);
    thumpLp.connect(thumpGain);
    thumpGain.connect(master);
    thumpSrc.start();

    // --- stall warner -----------------------------------------------------
    // A real 172's is a reed horn fed by suction from a port on the leading
    // edge. A square wave with a little filtering is close enough to be
    // instantly recognisable, which is the whole job of the sound.
    const hornOsc = audio.createOscillator();
    hornOsc.type = 'square';
    hornOsc.frequency.value = 800;
    const hornLp = audio.createBiquadFilter();
    hornLp.type = 'lowpass';
    hornLp.frequency.value = 2400;
    const hornGain = audio.createGain();
    hornGain.gain.value = 0;
    hornOsc.connect(hornLp);
    hornLp.connect(hornGain);
    hornGain.connect(master);
    hornOsc.start();

    return {
      master,
      engineBus,
      engineLp,
      oscs,
      oscGains,
      engineNoiseBp,
      engineNoiseGain,
      windLp,
      windLpGain,
      windHp,
      windHpGain,
      rollLp,
      rollGain,
      thumpGain,
      hornGain,
      sources: [engineNoise, windSrc, rollSrc, thumpSrc],
      oscSources: [...oscs, hornOsc],
    };
  }

  // -------------------------------------------------------------------------
  // Public surface
  // -------------------------------------------------------------------------

  /**
   * Create (or un-suspend) the audio context. MUST be called from inside a
   * user gesture — a click or a keydown handler. Safe to call repeatedly.
   */
  function resume() {
    if (failed) return false;
    try {
      if (!ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) {
          failed = true;
          return false;
        }
        ctx = new AC();
        nodes = build(ctx);
      }
      if (ctx.state === 'suspended') ctx.resume();
      return ctx.state === 'running';
    } catch (err) {
      console.warn('[audio] unavailable:', err && err.message);
      failed = true;
      ctx = null;
      nodes = null;
      return false;
    }
  }

  function isRunning() {
    return !!(ctx && ctx.state === 'running' && !muted);
  }

  function setMuted(v) {
    muted = !!v;
  }

  function toggleMute() {
    muted = !muted;
    if (!muted) resume();
    return muted;
  }

  /**
   * Follow the aeroplane. Call once per frame, after the flight model steps.
   *
   * @param {number} dt seconds (unused by the audio thread, kept for symmetry
   *                    with every other update() in the project)
   * @param {Object} state flightModel state
   * @param {Object} [inputs] control snapshot; only `throttle` is read
   * @param {boolean} [paused] silence everything but do not tear the graph down
   */
  function update(dt, state, inputs, paused) {
    if (!ctx || !nodes || !state) return;
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const n = nodes;
    const silent = muted || paused === true;

    // --- master -----------------------------------------------------------
    n.master.gain.setTargetAtTime(silent ? 0 : cfg.masterGain, now, MASTER_TAU);
    if (silent) return;

    // --- engine -----------------------------------------------------------
    const rpm = clampNum(state.rpm, 0, 4000);
    const rpmFrac = clamp01((rpm - IDLE_RPM) / (MAX_RPM - IDLE_RPM));
    // Firing frequency: 4-cylinder 4-stroke = two power strokes per rev.
    const fire = Math.max(8, (rpm / 60) * 2);

    for (let i = 0; i < n.oscs.length; i += 1) {
      const mul = i === 0 ? 0.5 : i === 1 ? 1 : i === 2 ? 2 : 3;
      n.oscs[i].frequency.setTargetAtTime(fire * mul, now, ENGINE_TAU);
    }

    // Timbre opens up with power: 320 Hz at idle to 1.6 kHz at full chat.
    n.engineLp.frequency.setTargetAtTime(320 + 1300 * rpmFrac, now, ENGINE_TAU);
    n.engineNoiseBp.frequency.setTargetAtTime(fire * 4, now, ENGINE_TAU);
    n.engineNoiseGain.gain.setTargetAtTime(0.05 + 0.14 * rpmFrac, now, ENGINE_TAU);

    // Level. Idle is audible but not dominant; the curve is deliberately
    // shallow at the top so full power is not four times as loud as cruise.
    const engineLevel = rpm > 1 ? 0.16 + 0.3 * Math.pow(rpmFrac, 0.7) : 0;
    n.engineBus.gain.setTargetAtTime(engineLevel, now, ENGINE_TAU);

    // --- slipstream -------------------------------------------------------
    // Aerodynamic noise goes with dynamic pressure, i.e. with v squared, so
    // the level rises as v^2 and the brightness roughly linearly.
    const v = clampNum(state.airspeedMs, 0, 200);
    const vf = clamp01(v / WIND_REF_MS);
    const vf2 = vf * vf;
    n.windLp.frequency.setTargetAtTime(300 + 900 * vf, now, WIND_TAU);
    n.windLpGain.gain.setTargetAtTime(0.34 * vf2, now, WIND_TAU);
    n.windHp.frequency.setTargetAtTime(800 + 2600 * vf, now, WIND_TAU);
    n.windHpGain.gain.setTargetAtTime(0.16 * vf2 * vf, now, WIND_TAU);

    // --- ground roll ------------------------------------------------------
    const onGround = state.onGround === true;
    const groundSpeed = onGround ? Math.min(v, 80) : 0;
    n.rollGain.gain.setTargetAtTime(
      onGround ? 0.03 + 0.3 * clamp01(groundSpeed / 45) : 0,
      now,
      0.08,
    );
    n.rollLp.frequency.setTargetAtTime(160 + 5 * groundSpeed, now, 0.1);

    // --- touchdown --------------------------------------------------------
    if (onGround && !wasOnGround) {
      // Energy from the descent rate we arrived with, not from the current
      // one — by the time onGround flips, the gear has already stopped it.
      const sink = Math.min(Math.abs(lastVsMs), 6);
      const level = 0.12 + 0.55 * (sink / 6);
      try {
        n.thumpGain.gain.cancelScheduledValues(now);
        n.thumpGain.gain.setValueAtTime(level, now);
        n.thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
      } catch {
        /* a cancelled ramp is not worth a crash */
      }
    }
    wasOnGround = onGround;
    if (!onGround) lastVsMs = clampNum(state.verticalSpeedMs, -50, 50);

    // --- stall warner -----------------------------------------------------
    // Latch on the buffet warning, not on the break: by the time the wing has
    // actually let go, a horn is telling you something you already know.
    const wantHorn = state.stallWarning === true || state.stalled === true;
    if (wantHorn !== stallOn) {
      stallOn = wantHorn;
      n.hornGain.gain.setTargetAtTime(wantHorn ? 0.09 : 0, now, 0.04);
    }
  }

  /** Tear the graph down and close the context. */
  function dispose() {
    if (!ctx) return;
    try {
      if (nodes) {
        for (const s of nodes.sources) s.stop();
        for (const o of nodes.oscSources) o.stop();
        nodes.master.disconnect();
      }
      ctx.close();
    } catch {
      /* closing a dead context is fine */
    }
    ctx = null;
    nodes = null;
  }

  return { update, resume, setMuted, toggleMute, isMuted: () => muted, isRunning, dispose };
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : Number.isFinite(v) ? v : 0;
}

function clampNum(v, lo, hi) {
  if (!Number.isFinite(v)) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}
